// MJOS-147 — assistant-ui Thread for the MyJarvis agent panel.
//
// Headless assistant-ui primitives + Tailwind. Renders the message viewport
// (user + assistant bubbles, streaming text), a tool-call chip, and a bottom
// composer (textarea + send button). Plain text rendering — no markdown for v1.
import { type FC, useState } from "react";
import {
	ThreadPrimitive,
	MessagePrimitive,
	ComposerPrimitive,
	type ToolCallMessagePartProps,
	type TextMessagePartComponent,
} from "@assistant-ui/react";
import {
	AlertTriangle,
	Check,
	ChevronRight,
	Loader2,
	SendHorizonal,
	Square,
	Wrench,
} from "lucide-react";
import { cn } from "@/lib/utils";

const TextPart: TextMessagePartComponent = ({ text }) => (
	<span className="whitespace-pre-wrap break-words">{text}</span>
);

// snake_case / camelCase tool name → human Title Case.
function humanize(name: string): string {
	return name
		.replace(/[_-]+/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.replace(/\b\w/g, (c) => c.toUpperCase())
		.trim();
}

// One short phrase for the collapsed line. Generic by default; for the `zeus`
// tool we surface the chosen method (it lives in the args) so the line reads
// "Zeus · Stations" rather than a bare "Zeus".
function friendlyLabel(toolName: string, argsText?: string): string {
	if (toolName === "zeus" && argsText) {
		try {
			const a = JSON.parse(argsText);
			if (a?.method) return `Zeus · ${a.method}`;
		} catch {
			/* args may still be streaming — fall through */
		}
		return "Zeus";
	}
	return humanize(toolName);
}

// Tool calls render as a single collapsed line: a status icon + a short label,
// click to expand the raw args + result. One generic component for EVERY tool
// (assistant-ui `tools.Fallback` slot), so new tools get the same clean line
// with no extra code. Purely presentational — the agent receives the full tool
// result independently; this only controls what the human sees.
const ToolFallback: FC<ToolCallMessagePartProps> = ({
	toolName,
	argsText,
	result,
	isError,
	status,
}) => {
	const [open, setOpen] = useState(false);

	const errored = isError || status?.type === "incomplete";
	const done = !errored && result != null;
	const running = !errored && !done;

	const StatusIcon = errored ? AlertTriangle : running ? Loader2 : done ? Check : Wrench;
	const statusClass = errored
		? "text-destructive"
		: running
			? "animate-spin text-muted-foreground"
			: done
				? "text-emerald-600"
				: "text-muted-foreground";

	const resultText =
		result == null
			? ""
			: typeof result === "string"
				? result
				: JSON.stringify(result, null, 2);

	return (
		<div className="my-1 rounded-md border bg-muted/40 text-xs text-muted-foreground">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left"
			>
				<StatusIcon className={cn("h-3.5 w-3.5 shrink-0", statusClass)} />
				<span className="min-w-0 flex-1 truncate font-medium text-foreground/80">
					{friendlyLabel(toolName, argsText)}
				</span>
				<ChevronRight
					className={cn("h-3 w-3 shrink-0 transition-transform", open && "rotate-90")}
				/>
			</button>
			{open ? (
				<div className="border-t border-border/60 px-2.5 py-1.5">
					{argsText ? (
						<pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-snug opacity-80">
							{argsText}
						</pre>
					) : null}
					{resultText ? (
						<pre
							className={cn(
								"mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-snug",
								errored ? "text-destructive" : "opacity-90",
							)}
						>
							{resultText}
						</pre>
					) : null}
				</div>
			) : null}
		</div>
	);
};

const messageComponents = {
	Text: TextPart,
	tools: { Fallback: ToolFallback },
} as const;

const UserMessage: FC = () => (
	<MessagePrimitive.Root className="mb-3 flex justify-end">
		<div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2 text-sm text-primary-foreground">
			<MessagePrimitive.Content components={messageComponents} />
		</div>
	</MessagePrimitive.Root>
);

const AssistantMessage: FC = () => (
	<MessagePrimitive.Root className="mb-3 flex justify-start">
		<div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-secondary px-3.5 py-2 text-sm text-secondary-foreground">
			<MessagePrimitive.Content components={messageComponents} />
		</div>
	</MessagePrimitive.Root>
);

const threadComponents = { UserMessage, AssistantMessage } as const;

export function Thread() {
	return (
		<ThreadPrimitive.Root className="flex h-full flex-col bg-background">
			<ThreadPrimitive.Viewport className="flex-1 overflow-y-auto px-3 py-4">
				<ThreadPrimitive.Empty>
					<div className="flex h-full min-h-[120px] items-center justify-center px-6 text-center text-sm text-muted-foreground">
						Ask MyJarvis about your dashboard, projects, tickets, or knowledge base.
					</div>
				</ThreadPrimitive.Empty>
				<ThreadPrimitive.Messages components={threadComponents} />
			</ThreadPrimitive.Viewport>

			<Composer />
		</ThreadPrimitive.Root>
	);
}

function Composer() {
	return (
		<ComposerPrimitive.Root className="flex items-end gap-2 border-t bg-background p-2.5">
			<ComposerPrimitive.Input
				rows={1}
				autoFocus
				placeholder="Message MyJarvis…"
				className={cn(
					"max-h-40 min-h-[40px] flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm",
					"placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
				)}
			/>
			{/* Swap Send → Stop while a run is in flight. Cancel calls the runtime's
			    onCancel, which is wired to pi's Agent.abort(). */}
			<ThreadPrimitive.If running={false}>
				<ComposerPrimitive.Send
					className={cn(
						"flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground",
						"transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40",
					)}
					aria-label="Send message"
				>
					<SendHorizonal className="h-[18px] w-[18px]" />
				</ComposerPrimitive.Send>
			</ThreadPrimitive.If>
			<ThreadPrimitive.If running>
				<ComposerPrimitive.Cancel
					className={cn(
						"flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground",
						"transition-colors hover:bg-secondary/80",
					)}
					aria-label="Stop generating"
				>
					<Square className="h-[16px] w-[16px]" />
				</ComposerPrimitive.Cancel>
			</ThreadPrimitive.If>
		</ComposerPrimitive.Root>
	);
}
